import * as Sentry from "@sentry/node";
import { apiFetchJson } from "utils";
import { getCheckoutSession } from "lib/api/checkout";
import type { CheckoutSession } from "lib/api/checkout";
import type { ParsedErrorResponse } from "lib/api/base";

/**
 * App-wide SSR data.
 */

type Settled<T> = { ok: true; value: T } | { ok: false; reason: unknown };

const settle = <T>(promise: Promise<T>): Promise<Settled<T>> =>
  promise.then(
    (value) => ({ ok: true as const, value }),
    (reason) => ({ ok: false as const, reason })
  );

const reportFailure = (label: string, reason: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`SSR fetch for ${label} failed:`, reason);
  Sentry.captureException(reason);
};

export async function getAppInitialData(
  ctx: Parameters<typeof getCheckoutSession>[0]
) {
  const [settings, siteBanners, featureFlags, checkoutSession] =
    await Promise.all([
      settle<Record<string, unknown>>(apiFetchJson("/api/settings/")),
      settle(apiFetchJson("/api/site-banners/")),
      settle(apiFetchJson("/api/feature-flags/")),
      settle<CheckoutSession | ParsedErrorResponse>(getCheckoutSession(ctx)),
    ]);

  (
    [
      ["settings", settings],
      ["site banners", siteBanners],
      ["feature flags", featureFlags],
      ["checkout session", checkoutSession],
    ] as const
  ).forEach(([label, result]) => {
    if (!result.ok) {
      reportFailure(label, result.reason);
    }
  });

  // settings and featureFlags are critical; throw a render failure if their
  // fetches fail.
  if (!settings.ok) {
    throw settings.reason;
  }
  if (!featureFlags.ok) {
    throw featureFlags.reason;
  }

  // siteBanners and checkoutSession (used client-side only for facebook pixel
  // matching) are non-critical; use fallbacks if their fetches fail.
  return {
    settings: settings.value,
    featureFlags: featureFlags.value,
    siteBanners: siteBanners.ok ? siteBanners.value : null,
    checkoutSession: checkoutSession.ok ? checkoutSession.value : {},
  };
}
